import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/app-shell.default.js';
import * as wireframeSkin from './skins/app-shell.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

class RunAppShell extends RunElement {
  #navOpen = false;
  #rendering = false;
  #delegated = false;
  #sideNavSlot = null;
  #renderedHasSideNav = null;
  #resizeObs = null;

  static get observedAttributes() {
    return ['nav-open'];
  }

  _onShadowClickBound = (e) => this.#handleShadowClick(e);
  _onDocKeydownBound = (e) => this.#handleDocKeydown(e);
  // Guarded: only re-render when side-nav presence ACTUALLY changed since the
  // last render. Without this guard, slotchange (which fires after every
  // render, on the freshly-created slot) → requestUpdate → render → new slot
  // → slotchange → infinite loop that hangs the renderer.
  _onSlotChangeBound = () => {
    if (this._hasSideNav !== this.#renderedHasSideNav) this.requestUpdate();
  };

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'none');
    document.addEventListener('keydown', this._onDocKeydownBound);
    // Resolve docked vs drawer from the component's OWN width vs the
    // configurable --run-shell-breakpoint token. CSS @media/@container
    // cannot read custom properties, so the breakpoint decision is owned
    // by the headless (behaviour), not the skin (presentation). Computed
    // synchronously before first render to avoid a layout-mode flash.
    this.#evaluateLayout();
    this.#resizeObs = new ResizeObserver(() => this.#evaluateLayout());
    this.#resizeObs.observe(this);
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onDocKeydownBound);
    this.#resizeObs?.disconnect();
    this.#resizeObs = null;
    if (this.#sideNavSlot) {
      this.#sideNavSlot.removeEventListener('slotchange', this._onSlotChangeBound);
      this.#sideNavSlot = null;
    }
  }

  // Behavioural decision: which layout mode applies. Reads the consumer-set
  // --run-shell-breakpoint token (default 1024px) and the host's own box
  // width, then drives a discrete state hook the query-free skin keys off.
  #evaluateLayout() {
    const raw = getComputedStyle(this).getPropertyValue('--run-shell-breakpoint');
    const bp = parseFloat(raw) || 1024;
    const width = this.getBoundingClientRect().width || this.offsetWidth;
    const mode = width > 0 && width < bp ? 'drawer' : 'docked';
    if (this.getAttribute('data-layout') !== mode) {
      this.setAttribute('data-layout', mode);
      // A docked shell has no drawer to leave open.
      if (mode === 'docked' && this.#navOpen) this.navOpen = false;
      this.requestUpdate();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (name === 'nav-open') this.navOpen = newValue !== null;
  }

  get navOpen() { return this.#navOpen; }
  set navOpen(value) {
    const coerced = Boolean(value);
    if (this.#navOpen === coerced) return;
    this.#navOpen = coerced;
    this.toggleAttribute('nav-open', coerced); // drives :host([nav-open]) drawer + scrim CSS
    this.requestUpdate();
    this.dispatchEvent(new CustomEvent('run-shell-nav-open', {
      bubbles: true,
      composed: true,
      detail: { open: coerced },
    }));
  }

  // Internal-only (contract): derived from whether side-nav slot has content.
  // Queried from light DOM so it is correct at skin render time without a
  // slotchange race.
  get _hasSideNav() {
    return !!this.querySelector(':scope > [slot="side-nav"]');
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
    // Record the side-nav presence the skin just rendered against, so the
    // slotchange handler can tell a real change from a post-render echo.
    this.#renderedHasSideNav = this._hasSideNav;

    // Scrim click closes the drawer — handler attached by the base (contract).
    // Delegated on the shadow root, which survives the skin's innerHTML swaps.
    if (!this.#delegated) {
      this.shadowRoot.addEventListener('click', this._onShadowClickBound);
      this.#delegated = true;
    }

    // Re-bind slotchange on the (recreated) side-nav slot so _hasSideNav-driven
    // aria-hidden re-renders when side-nav content is added/removed.
    const navSlot = this.shadowRoot.querySelector('slot[name="side-nav"]');
    if (navSlot && navSlot !== this.#sideNavSlot) {
      if (this.#sideNavSlot) {
        this.#sideNavSlot.removeEventListener('slotchange', this._onSlotChangeBound);
      }
      navSlot.addEventListener('slotchange', this._onSlotChangeBound);
      this.#sideNavSlot = navSlot;
    }
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  #handleShadowClick(event) {
    const hitScrim = event.composedPath().some(
      (n) => n.nodeType === 1 && n.classList?.contains('shell-scrim'),
    );
    if (hitScrim) this.navOpen = false;
  }

  #handleDocKeydown(event) {
    if (event.key === 'Escape' && this.#navOpen) {
      this.navOpen = false;
    }
  }
}

if (!customElements.get('run-app-shell')) {
  customElements.define('run-app-shell', RunAppShell);
}

export { RunAppShell };
export default RunAppShell;
